/**
 * GameState — the authoritative sim shape.
 *
 * Per MECHANICS.md §1.1 (determinism rules) and §3 (campaign entities).
 * v0M2.3 authors the skeleton; entity fields flesh out per-milestone
 * as consuming systems arrive. Every field must be JSON-serialisable
 * (no functions, no Symbol keys, no cycles) so that the future save
 * subsystem can `structuredClone` / `JSON.stringify` state directly.
 *
 * Branded ids per SG pattern: prevents accidental "used a SettlementId where
 * a FactionId was expected" bugs at compile time with zero runtime cost.
 */

import type { RngState } from './rng';
import type { BattleId as BattleIdImport } from './battle-state';
// Session-27 (2026-07-24): armyEffectiveSpeed reads xlsx-authoritative
// per-cohort moveSpeedCampaign. loadCohorts() parses dials.json (already
// loaded once at module init by the loader), so the eager import here
// adds no meaningful runtime cost.
import { loadCohorts } from '../data/cohorts';

// ---------------------------------------------------------------------------
// Branded identifier types
// ---------------------------------------------------------------------------

export type FactionId = number & { readonly __brand: 'FactionId' };
export type ProvinceId = number & { readonly __brand: 'ProvinceId' };
export type SettlementId = number & { readonly __brand: 'SettlementId' };
export type ArmyId = number & { readonly __brand: 'ArmyId' };
export type CohortId = number & { readonly __brand: 'CohortId' };
export type CommanderId = number & { readonly __brand: 'CommanderId' };
/** v0M5.3 (2026-07-25) — Standalone special-unit entity id (Spy at v1). */
export type SpyId = number & { readonly __brand: 'SpyId' };
/** v0M7.1 (2026-07-25) — World-buildable entity id (Watch Tower, Road,
 *  Bridge, Fort) per MAP.md §9. */
export type WorldStructureId = number & { readonly __brand: 'WorldStructureId' };

export const toFactionId = (n: number): FactionId => n as FactionId;
export const toProvinceId = (n: number): ProvinceId => n as ProvinceId;
export const toSettlementId = (n: number): SettlementId => n as SettlementId;
export const toArmyId = (n: number): ArmyId => n as ArmyId;
export const toCohortId = (n: number): CohortId => n as CohortId;
export const toCommanderId = (n: number): CommanderId => n as CommanderId;
export const toSpyId = (n: number): SpyId => n as SpyId;
export const toWorldStructureId = (n: number): WorldStructureId => n as WorldStructureId;

// ---------------------------------------------------------------------------
// Campaign entities (skeleton — expand per system as milestones land)
// ---------------------------------------------------------------------------

/**
 * Faction resources — 7 stockpile ledgers per ECONOMY.md §1 (v0M1.9.16).
 * Population is per-province (see Province.population), not on this ledger.
 * All values are non-negative floats (accumulated over sub-second ticks).
 */
export interface Resources {
  gold: number;
  iron: number;
  wood: number;
  horses: number;
  supplies: number;
  learning: number;
}

export function createEmptyResources(): Resources {
  return { gold: 0, iron: 0, wood: 0, horses: 0, supplies: 0, learning: 0 };
}

/** Faction — per MECHANICS.md §3. Team assignment lives here (§7). */
export interface Faction {
  readonly id: FactionId;
  readonly teamId: number; // #7 — immutable through the campaign
  /**
   * True if this faction is driven by the AI system (v0M3.8). False for
   * the human player + neutrals. Set at scenario bootstrap from
   * `SlotConfig.role === 'ai'` and never mutated during the campaign.
   */
  readonly isAI: boolean;
  /**
   * The faction's capital city (v0M3.9). Set at scenario bootstrap to
   * the Settlement entity created from the faction's largest-pop city at boot
   * time. Used by the victory system under `destroy-capitals` — a
   * faction is "capital held" iff the province containing this Settlement is
   * still owned by the faction. Optional because existing test fixtures
   * don't set it; victory-check treats null as "no capital, faction
   * excluded from destroy-capitals check."
   */
  readonly capitalSettlementId?: SettlementId;
  /** Resource stockpiles per ECONOMY.md §1. Mutated by economy system each tick. */
  resources: Resources;
  /**
   * v0M9.3 (2026-07-24) — Names of knowledges this faction has completed
   * researching. Order = completion order (append-only during a
   * campaign; no unlearn at v1). Consulted by Effect wiring per-hook
   * (max-army cap bonus, structure-unlock gates, order-catalogue
   * expansion, ...). Serializable as a plain string array so save/load
   * stays JSON-friendly per SG determinism discipline.
   *
   * Optional for backward-compat with existing test fixtures — consumers
   * should coalesce with `?? []`. Scenario bootstrap initializes to [].
   */
  researchedKnowledge?: string[];
  /**
   * v0M9.3 (2026-07-24) — Currently active research, or null if the
   * faction is not researching anything. One-active-at-a-time per the
   * v0M9 design lock; `start_research` rejects if this is non-null.
   * Progress advances by DAYS_PER_TICK per sim tick via `research.ts`;
   * on reaching `researchDays`, the knowledge moves into
   * `researchedKnowledge` and this field is nulled.
   *
   * Optional for backward-compat — treat `undefined` as `null`
   * (not researching). Scenario bootstrap initializes to null.
   */
  activeResearch?: ActiveResearch | null;
  /**
   * v0M6.3 (2026-07-26) — Ordered queue of knowledge names awaiting
   * research. When `activeResearch` becomes null (start of tick after
   * completion, or manual cancel), the research system pops the first
   * queued name, checks the faction has sufficient Learning, deducts
   * the cost, and promotes it to `activeResearch`. If Learning is
   * insufficient at promotion time, the queue pauses (item stays at
   * head) until Learning is available.
   *
   * Cap enforced at command-issue time via `research_queue_cap` dial
   * (default 5). Queue is validated for uniqueness: no already-active,
   * already-researched, or already-queued items may be enqueued.
   *
   * Optional for back-compat — treat `undefined` as empty. Scenario
   * bootstrap does not need to initialize this field.
   */
  researchQueue?: string[];
  // Victory progress, race+culture+traits (#14) added per system.
}

/**
 * v0M9.3 (2026-07-24) — Faction-level active research entry. The
 * Learning cost is deducted at `start_research` time (not on complete),
 * so a canceled research (post-MVP feature) does not refund learning.
 */
export interface ActiveResearch {
  /** Matches a key in `loadKnowledge()`. */
  readonly knowledgeName: string;
  /** Accumulated game-day progress toward completion. */
  progressDays: number;
}

/**
 * A structure built in a Province per ECONOMY.md §3.1. Every structure
 * produces exactly one resource type at a fixed rate per second per
 * `Balance.xlsx` Structures sheet (loaded via `src/data/structures.ts`).
 */
export interface StructureBuild {
  /** Structure name — matches a key in `loadStructures()`. */
  readonly name: string;
  /** Game-tick at which the structure was completed. */
  readonly builtAtTick: number;
  /**
   * v0M9 (2026-07-27) — Optional attribution to a specific settlement
   * within the containing province. Absent means "at the Province
   * Capital" (back-compat for pre-v0M9 fixtures + the majority case).
   * When set to a minor Settlement's id, the economy income pass will
   * halt this structure's income while that minor is `pillaged`.
   */
  readonly settlementId?: SettlementId;
}

/**
 * A structure currently under construction in a Province's build queue.
 * Progress advances each tick by SECONDS_PER_TICK; on reaching the
 * structure's buildTimeSec, the entry moves from queue to `structures`.
 */
export interface StructureBuildOrder {
  readonly name: string;
  /** Accumulated game-time seconds of progress toward completion. */
  progressSec: number;
  /**
   * v0M9 (2026-07-27) — Optional attribution to a specific minor
   * settlement within the containing province (else Province Capital).
   * Propagates to the resulting `StructureBuild.settlementId` on
   * completion.
   */
  readonly settlementId?: SettlementId;
}

/**
 * A cohort recruitment order in a Province's recruit queue (v0M3.2a).
 * Progress advances by SECONDS_PER_TICK each economy tick; on reaching
 * the cohort's recruitTimeSec from CohortSpec, the entry is removed +
 * a Cohort entity is spawned.
 *
 * v0M4.2.1 extension: three target modes, discriminated by which of
 * `armyId` / `settlementId` is non-null:
 *   1. Attach to existing army: `armyId !== null, settlementId === null`
 *   2. Create new army + Commander at completion: `armyId === null, settlementId === null`
 *   3. Garrison for city: `armyId === null, settlementId !== null` (BATTLES.md §3.2)
 * Invariant enforced at command validation: `armyId` and `settlementId` are
 * mutually exclusive — never both non-null.
 */
export interface RecruitmentOrder {
  /** Cohort type name — matches a key in loadCohorts() (Balance.xlsx Cohorts sheet).
   *  Empty string when this order is a special-unit recruit (v0M5.4d) — the
   *  presence of `specialUnitType` acts as the discriminator. */
  readonly cohortType: string;
  /** Target existing Army — non-null only in mode 1 above. */
  readonly armyId: ArmyId | null;
  /** Target Settlement for garrison recruit (v0M4.2.1) — non-null only in mode 3 above.
   *  For special-unit (v0M5.4d) recruits this is the settlement where the
   *  unit will spawn on completion — always non-null. */
  readonly settlementId: SettlementId | null;
  /**
   * v0M5.4d (2026-07-25) — SpecialUnits xlsx row key (e.g. 'Spy'). When
   * set, `stepRecruitQueue` treats this order as a special-unit recruit:
   * completion threshold from SpecialUnitSpec.recruitTimeDays × TICKS_PER_GAME_DAY
   * × SECONDS_PER_TICK; on completion, spawns a Spy at the settlementId's
   * cell instead of a Cohort. Optional so existing cohort-only fixtures
   * compile unchanged.
   */
  readonly specialUnitType?: string;
  /**
   * v0M7.4d+garrison (2026-07-26) — Fort target for a fort-garrison
   * recruit. Non-null selects fort-garrison mode: finished cohort
   * spawns as a garrison of the given Fort with `worldStructureId`
   * set + joins the Fort's `garrisonCohortIds`. Mutually exclusive
   * with `armyId` + `settlementId` + `specialUnitType`. Optional for
   * back-compat with pre-v0M7.4d fixtures.
   */
  readonly worldStructureId?: WorldStructureId | null;
  /** Accumulated game-time seconds of progress toward completion. */
  progressSec: number;
}

/** Province tier per ECONOMY.md §2.1 — determines pop cap + structure cap. */
export type SettlementTier = 'Village' | 'Town' | 'City';

/** Province — the political atom (#15). Expanded at v0M3.2 for the economy layer. */
export interface Province {
  readonly id: ProvinceId;
  ownerFactionId: FactionId | null;
  /** Current population — mutated by growth + capture per ECONOMY.md §2.2. */
  population: number;
  /** Settlement tier per ECONOMY.md §2.1. Determines pop + structure caps. */
  tier: SettlementTier;
  /** Structures completed in this province. */
  structures: StructureBuild[];
  /** Structures currently under construction. */
  buildQueue: StructureBuildOrder[];
  /** Cohorts currently being recruited per ECONOMY.md §4 + v0M3.2a lock. */
  recruitQueue: RecruitmentOrder[];
  // Cells (grouping), borders, unrest, trade added per system.
}

/**
 * Active-siege state on a Settlement (v0M4.2.2 per BATTLES.md §3.1 / §3.4).
 * `null` on Settlement.siegeState means no active siege.
 *
 * Managed by `sieges.ts`:
 *   - Opened when a hostile army moves adjacent to the city cell and no
 *     defending field army is present in the same province.
 *   - Countdown decrements by `1 / TICKS_PER_GAME_DAY` per tick.
 *   - Cleared when: sieger leaves adjacency, sieger destroyed, defender
 *     field army contacts sieger, or `daysRemaining ≤ 0` (capture path).
 */
export interface SiegeState {
  /** Army that opened / holds the siege. */
  readonly siegingArmyId: ArmyId;
  /** Game tick at which the current siege opened. Reset each open. */
  readonly siegeSinceTick: number;
  /** Game-days remaining before starve-out capture. Decrements each tick. */
  daysRemaining: number;
}

/**
 * v0M9 (2026-07-27) — Active pillage timer on a minor settlement per
 * MECHANICS.md §2 v0M9 amendment. `null`/absent means no active
 * pillage attempt.
 *
 * Managed by `pillages.ts`:
 *   - Opened when a hostile Army enters the minor settlement's ZoC
 *     AND either the settlement has no garrison OR the garrison has
 *     been broken (garrison siege battle path handled by sieges.ts).
 *   - `daysElapsed` increments by `1/TICKS_PER_GAME_DAY` per tick
 *     while at least one hostile army remains in ZoC.
 *   - On reaching `MINOR_SETTLEMENT_PILLAGE_DAYS`: `Settlement.pillaged`
 *     set to true, this state cleared. Minor settlements cannot be
 *     captured — ownership stays with the province owner. Only structure
 *     income at the pillaged minor is halted (economy.ts).
 *   - Cleared (reset) if no hostile army remains in ZoC — pillage
 *     requires continuous enemy presence (raider model).
 */
export interface PillageState {
  /** Faction whose army opened / holds the pillage attempt (any-enemy
   *  semantics — first enemy in ZoC opens; if it leaves and another
   *  enemy remains, the timer continues). */
  readonly byFactionId: FactionId;
  /** Accumulated game-days of enemy presence in ZoC. */
  daysElapsed: number;
}

/**
 * v0M9 (2026-07-27) — Active repair timer on a pillaged minor
 * settlement. `null`/absent means no active repair attempt.
 *
 * Managed by `repair.ts`:
 *   - Opened by `repair_settlement` command from the province owner;
 *     Gold cost (`MINOR_SETTLEMENT_REPAIR_GOLD_COST`) deducted at
 *     open time.
 *   - `daysElapsed` increments by `1/TICKS_PER_GAME_DAY` per tick
 *     ONLY while no hostile army is in the minor's ZoC (pauses on
 *     enemy entry; resumes when clear).
 *   - On reaching `MINOR_SETTLEMENT_REPAIR_DAYS`: `Settlement.pillaged`
 *     set to false, this state cleared. Structure income resumes.
 *   - Cancelable via `cancel_repair_settlement`; Gold NOT refunded
 *     (matches research-cancel pattern from v0M6.3).
 */
export interface RepairState {
  /** Accumulated game-days of repair work (pauses while enemy in ZoC). */
  daysElapsed: number;
}

/** Settlement — one per province at a fixed site. */
export interface Settlement {
  readonly id: SettlementId;
  readonly provinceId: ProvinceId;
  /**
   * The cell this city sits on in map coordinates (v0M3.11). Consumed by
   * `economy.ts` `spawnCohort` to place a newly recruited army at the
   * city's site (rather than the pre-v0M3.11 hardcoded (0, 0)). Set at
   * scenario bootstrap from the `ScenarioCity.cell` picked as the
   * faction's capital.
   */
  readonly cell: { readonly x: number; readonly y: number };
  /**
   * Settlement-attached garrison cohorts (v0M4.2.1 per BATTLES.md §3.2). Ids of
   * Cohort entities in `state.cohorts` whose `owner.kind === 'city'` and
   * `owner.settlementId === this.id`. Garrisons never move; they exist only to
   * defend the city during siege battles. Starting garrison seeded at
   * scenario boot per `garrison_starting_count`; player may recruit up to
   * `garrison_max_per_settlement` (both dials in Balance.xlsx).
   */
  garrisonCohortIds: CohortId[];
  /**
   * Active siege on this city (v0M4.2.2 per BATTLES.md §3). `null` when
   * no siege. Semantics per `SiegeState`. Enforced by `sieges.ts`;
   * consumed by `economy.ts` to gate production while under siege
   * (§3.4 resource-denial rule).
   */
  siegeState: SiegeState | null;
  /**
   * v0M5.1 (2026-07-25) — Vision radius (in strategic-map cells) this
   * Settlement contributes to its owner's `Visibility` map per
   * `MAP.md` §7. Terrain-modified during BFS propagation
   * (`visibility.ts`). Optional so pre-v0M5.1 test fixtures compile;
   * the visibility system falls back to `settlement_vision_range`
   * dial when this field is absent. Owner-only — neutral settlements
   * contribute nothing.
   */
  visionRange?: number;
  /**
   * MFix6.6.1 (2026-07-23) — Manpower pool. Fed by population at
   * `manpower_generation_per_pop_per_day`, capped at
   * `population × manpower_cap_fraction_of_pop`. Drawn on for cohort
   * recruitment (1 manpower per figure the cohort fields) and figure
   * replenishment (per `manpower_replenish_figures_per_day_per_cohort`
   * for cohorts inside this city's ZoC). Race-tagged (see
   * `manpowerRace`) so future post-MVP fantasy content can have
   * different races drawing from different pools.
   *
   * Optional so pre-MFix6.6.1 test fixtures can construct Settlement
   * without setting it; production scenario boot always populates.
   * Consumers should read with a `?? 0` fallback.
   */
  manpower?: number;
  /**
   * MFix6.6.1 (2026-07-23) — race tag on this city's manpower pool.
   * v1 always 'Human'. Post-MVP fantasy expansion may split the pool
   * into multiple race-keyed sub-pools; keeping the tag as a string
   * here future-proofs the data shape. Consumers should read with a
   * `?? 'Human'` fallback.
   */
  manpowerRace?: string;
  /**
   * v0M9 (2026-07-27) — True if this settlement is a "minor" (Other
   * Province Settlement per MECHANICS.md §3). Materialized from the
   * `province.minorCities[]` map data at scenario boot. Owner always
   * follows the containing Province (never independent). Absent /
   * false = Province Capital.
   */
  isMinor?: boolean;
  /**
   * v0M9 (2026-07-27) — Icon-flavor tag inherited from map-data
   * `RawMinorCity.kind` — one of 'mine' / 'farm' / 'workshop' /
   * 'trade_post' / 'shrine'. **Cosmetic only** — no sim system branches
   * on this value (v0M2.6c-era mechanical effect retired at v0M9 per
   * Michael's design lock). Absent for capitals + fixtures.
   */
  kind?: string;
  /**
   * v0M9 (2026-07-27) — True after the pillage timer completes. While
   * true, structures at this settlement produce no income (economy.ts).
   * Cleared by a successful Repair. Absent / false = functional.
   */
  pillaged?: boolean;
  /**
   * v0M9 (2026-07-27) — Active pillage-timer state. Non-null when an
   * enemy army is presently draining the pillage timer. Cleared when
   * the timer completes (setting `pillaged` true) or when enemy leaves
   * ZoC (raider-model reset). Absent / null on capitals and unpillaged
   * minors.
   */
  pillageState?: PillageState | null;
  /**
   * v0M9 (2026-07-27) — Active repair-timer state. Non-null while the
   * province owner is repairing this pillaged minor. Ticks only when
   * no enemy in ZoC. Absent / null when no repair in flight.
   */
  repairState?: RepairState | null;
  /**
   * v0M9 (2026-07-27) — Structure cap for this settlement. For minors,
   * seeded from `MINOR_SETTLEMENT_STRUCTURE_CAP` (default 2) at boot;
   * for capitals, absent — capital cap is derived from Province tier
   * per ECONOMY.md §2.1 by the existing code path.
   */
  structureCap?: number;
  // Population, buildings, build queue added per system.
}

/** Commander — Army-scope stat carrier per BATTLES.md §2.7 and UNITS.md §4.
 *
 *  v0M1.9.15 Default commander model: no combat/morale bonuses; only carries
 *  level + cohort-command capacity + XP (progression). Hero commanders (post-MVP)
 *  return the star + traits model. */
export interface Commander {
  readonly id: CommanderId;
  /** Commander level per BATTLES.md §2.7.8.7.d. Level 1 = 6 cohort limit. */
  level: number;
  /** Experience earned via battles; XP formula deferred (§2.7.8.10 item 27). */
  xp: number;
}

/** Create a new Default commander at level 1 per BATTLES.md §2.7.8.7. */
export function createCommander(id: CommanderId): Commander {
  return { id, level: 1, xp: 0 };
}

/**
 * True iff this cohort is a city garrison (v0M4.2.1). Garrison cohorts
 * have `settlementId !== null` and `armyId === null`; army cohorts are the
 * inverse. Callers use this to skip garrisons in army-oriented loops
 * (e.g. `stepCohortRecovery`, `resolveBattleConclusion`), or to route
 * garrison-specific writeback (e.g. siege-battle casualty writeback
 * lands on the owning city, not an army — v0M4.2.5 scope).
 */
export function isGarrisonCohort(cohort: { readonly armyId: ArmyId | null; readonly settlementId: SettlementId | null }): boolean {
  return cohort.settlementId !== null;
}

/** Cohort command limit by commander level per BATTLES.md §2.7.8.7.d.
 *  Level 1 = 6 cohorts. Higher-level progression deferred to §2.7.8.10 item 27. */
export function commanderCohortLimit(level: number): number {
  // v0M4.0g lock (UNITS.md §5.3): +1 cohort slot per rank; rank 1 = 6.
  // Rank 20 = 25 = standard commander cap (UNITS.md §5.4). Hero commanders
  // (post-MVP §4.2) may extend the cap; the helper here is Default-only.
  const rank = Math.max(1, level);
  return Math.min(5 + rank, 25);
}

/**
 * Campaign-scope Cohort entity — the persistent record on the strategic map.
 * Distinct from the battlescape `CohortOnField` in `battle-state.ts`, which
 * carries battle-scoped mutable state (position, facing, morale, etc.).
 *
 * At battle start, a campaign Cohort hydrates into a CohortOnField by looking
 * up its `type` in the Cohorts sheet of `Balance.xlsx` (via `loadCohorts()`)
 * and copying the stat block + creating fresh figures.
 */
export interface Cohort {
  readonly id: CohortId;
  readonly factionId: FactionId;
  /**
   * Army this cohort belongs to on the strategic map, OR null if this is
   * a city garrison cohort (v0M4.2.1). Exactly one of `armyId` / `settlementId`
   * is non-null — see `isGarrisonCohort()` for the narrowing helper.
   */
  armyId: ArmyId | null;
  /**
   * Settlement this cohort garrisons (v0M4.2.1 per BATTLES.md §3.2), or null if
   * this is an army cohort. Non-null only for garrison cohorts. Garrisons
   * never move and only participate in siege battles at their home city.
   */
  settlementId: SettlementId | null;
  /**
   * v0M7.4d+garrison (2026-07-26) — Fort this cohort garrisons per
   * MAP.md §9.2. Non-null only for Fort-garrison cohorts. Mirrors the
   * settlement-garrison pattern above: fort garrisons never move and
   * only participate in Fort-assault battles. Exactly one of
   * `armyId` / `settlementId` / `worldStructureId` is non-null for a
   * live cohort.
   *
   * Optional field for back-compat with pre-v0M7.4d fixtures. Absence
   * means "not a Fort garrison".
   */
  worldStructureId?: WorldStructureId | null;
  /** Cohort type name — matches a key in loadCohorts() (Balance.xlsx Cohorts sheet). */
  readonly type: string;
  /** Current living-figure count. Persists between battles; decremented by casualties on battle close. */
  aliveFigureCount: number;
  /**
   * Persistent stamina (v0M3.11). Range [0, 100]. Spawns at 100; recovered on
   * the campaign map when the owning Army is not moving and not in battle
   * (see `economy.ts` step 5). Copied into `CohortOnField.stamina` at battle
   * start by `battle-factory.ts`; written back at battle close by
   * `battle-conclusion.ts` (writeback of final field value clamped [0, 100]).
   */
  stamina: number;
  /**
   * Persistent morale (v0M3.11). Range [0, 100]. Spawns at 100; recovered on
   * the campaign map when the owning Army is inside a friendly-team province
   * and not in battle. Same battle-round-trip semantics as `stamina`.
   */
  morale: number;
  /** Cohort veterancy XP (v0M3.2a placeholder — v0M3.2b commander/cohort XP integration). */
  xp: number;
  /**
   * MFix6.6.1 (2026-07-23) — cohort race. v1 always 'Human'. Post-MVP
   * fantasy expansion will introduce non-human races; a cohort can
   * only replenish figures from a city whose `manpowerRace` matches.
   * Hidden attribute for now — no UI surface at v1.
   *
   * Optional so pre-MFix6.6.1 test fixtures can construct Cohort
   * without setting it; production code populates. Consumers should
   * read with a `?? 'Human'` fallback.
   */
  cohortRace?: string;
  /**
   * v0M9.13 (2026-07-24) — permanent Armor bonus baked in at recruit
   * time from structures in the recruiting province (Armorers Guild
   * +5 at v1). Added on top of `spec.armor` at battle-factory time so
   * every combat round sees the boosted value. Persists for the
   * cohort's lifetime — retreating to a city with no Armorers Guild
   * does not remove the bonus.
   *
   * Optional so pre-v0M9.13 test fixtures compile; consumers should
   * treat undefined as 0.
   */
  armorBonus?: number;
  /**
   * v0M10.4 (2026-07-24) — Shared starvation-HP pool used by the phase-2
   * starvation drain. Undefined until the cohort enters phase 2 (its
   * morale AND stamina have both been drained to 0 by supply=0 ticks).
   * When first entered, seeded from spec.maxHealthPerFigure. Drains at
   * `starvation_hp_drain_per_day` per day while supplies=0. When it
   * hits 0, `aliveFigureCount` is zeroed and the cohort is destroyed.
   *
   * Reset to undefined whenever the owning army's supplies rise above
   * 0, so a resupply lets the cohort recover to normal state (morale +
   * stamina still recover via stepCohortRecovery on their own schedule).
   */
  starvationHp?: number;
}

/**
 * Army — the only entity that moves across the map per MECHANICS.md §3.1.
 * Composition, march stance, route preference, ZoC, operational stats
 * (visibility / morale / fatigue / supply) added per system.
 */
export interface Army {
  readonly id: ArmyId;
  readonly ownerFactionId: FactionId;
  readonly commanderId: CommanderId;

  /**
   * Continuous float position in cell units (v0M3.1). Rendered by
   * interpolation between tick states client-side. Armies are created
   * in Cities per MECHANICS.md §3.1 — scenario boot places them at the
   * founding city's cell centre.
   */
  position: { x: number; y: number };

  /**
   * Final march destination for the current move order, or `null` when
   * idle. Cleared when the Army arrives (position is within
   * `ARRIVAL_EPSILON` of the goal).
   */
  movementGoal: { x: number; y: number } | null;

  /**
   * Smoothed waypoint list from the current position toward the
   * `movementGoal`, in cell coords (LOS-smoothed by the pathfinder).
   * The head of the array is the next waypoint to reach; consumed
   * waypoints are dropped by the movement system as the Army passes
   * them. `null` when idle or when no path could be found.
   */
  plannedPath: readonly (readonly [number, number])[] | null;

  /**
   * MFix12.4 (2026-07-27) — chained player-set destinations queued after
   * the current `movementGoal`. Populated by shift+RMB waypoint mode
   * (see main.ts client handler + `move_army.append`). When the Army
   * arrives at `movementGoal`, the movement system pops
   * `waypoints[0]`, pathfinds to it, and sets it as the new
   * `movementGoal`. Empty / undefined = no chained waypoints.
   *
   * A plain (non-append) `move_army` clears this queue entirely so a
   * fresh single-destination order preempts any pending chain.
   */
  waypoints?: readonly { readonly x: number; readonly y: number }[];

  /**
   * Cohorts in this Army's roster (v0M3.2a). Ids of Cohort entities in
   * `state.cohorts`. Cohort-command limit enforced at recruit / merge
   * time per BATTLES.md §2.7.8.7.d.
   */
  cohortIds: CohortId[];

  /**
   * v0M10.1 (2026-07-24) — Army attachments. Each entry is a string
   * identifier from `AttachmentType` — 'Engineer', 'Healer', 'Supply
   * Wagon' at v1. Added via the `add_army_attachment` command when the
   * army sits in a friendly settlement's ZoC and the settlement has the
   * unlocking structure (Engineering Guild / Herbalist Guild / Merchant
   * Guild).
   *
   * Persistent — attachments stay with the army even after it leaves
   * the settlement's ZoC. No remove-attachment command at v1.
   *
   * Optional so pre-v0M10 test fixtures compile; consumers should
   * coalesce with `?? []`.
   */
  attachments?: string[];

  /**
   * v0M10.1 (2026-07-24) — Attachment slot cap. Default 1 (dial-driven);
   * later expansions may raise this via commander unlock or knowledge.
   * Cached on the Army rather than looked up dynamically so a future
   * mid-campaign cap change can preserve existing armies' slots.
   *
   * Optional so pre-v0M10 fixtures compile; missing = base dial (1).
   */
  attachmentCap?: number;

  /**
   * v0M10.1 (2026-07-24) — Current supply pool. Consumed at
   * `army_supply_consumption_per_figure_per_day` per figure per day
   * while outside a friendly settlement ZoC; regenerated at
   * `army_supply_regen_per_day` per day (from the owning faction's
   * global supplies stockpile) while inside a friendly ZoC. Capped at
   * `supplyCap`.
   *
   * Optional so pre-v0M10 fixtures compile; missing = full cap.
   */
  supplies?: number;

  /**
   * v0M10.1 (2026-07-24) — Per-army supply cap. Default dial value
   * (1200 at v0M10 lock). Cached so future supply-cap bonuses (e.g.
   * from a Merchant Guild attachment) can raise it per-army without
   * touching the dial.
   *
   * Optional so pre-v0M10 fixtures compile; missing = base dial.
   */
  supplyCap?: number;

  /**
   * Battle this Army is currently engaged in (v0M3.4), or null when
   * free to move on the strategic map. Set by `zoc-detection` when
   * enemy ZoC overlap triggers a Battle; cleared by `battles` when the
   * battle's result is set.
   *
   * Armies with a non-null `currentBattleId` do not advance movement
   * and cannot be caught by ZoC detection into a second battle.
   */
  currentBattleId: import('./battle-state').BattleId | null;

  /**
   * v0M5.1 (2026-07-25) — Vision radius (in strategic-map cells) this
   * Army contributes to its owner's `Visibility` map per `MAP.md` §7.
   * Terrain-modified during BFS propagation (`visibility.ts`).
   * Optional so pre-v0M5.1 test fixtures compile; missing = falls back
   * to `army_vision_range` dial.
   */
  visionRange?: number;

  /**
   * v0M7.1 (2026-07-25) — Active world-buildable build order per
   * `MAP.md` §9. Set when the player (or AI) issues Build X + a target
   * cell against an Army carrying the `Engineer` attachment. The build
   * system (`src/sim/build.ts`, v0M7.3) advances `progressDays` by
   * `1/TICKS_PER_GAME_DAY` each tick while the Army's centre is within
   * `world_build_arrival_epsilon` cells of `targetCell` AND it is not
   * in a battle. If the Army moves off (player command or forced
   * retreat), `progressDays` resets to zero next tick — the order
   * itself persists so the Army resumes progress if it returns to
   * `targetCell`. On reaching the per-kind `MapStructureSpec.buildTimeDays`
   * threshold, the corresponding `WorldStructure` is minted + this
   * field is cleared. Cancel via `cancel_army_build_order` command.
   *
   * Optional so pre-v0M7 fixtures compile; missing = no active order.
   */
  buildOrder?: BuildOrder | null;

  // ZoC radius, stance, route pref etc. added per system.
}

/**
 * v0M7.1 (2026-07-25) — Kind discriminator for world buildables. Matches
 * the row keys authored in the MapStructures xlsx sheet (Watch Tower,
 * Fort) plus the two dial-driven-only kinds (Road, Bridge — no
 * per-row state). Stored as lowercase for internal use; MAP.md §9.2 uses
 * the display names.
 */
export type WorldStructureKind = 'road' | 'watchtower' | 'bridge' | 'fort';

/** v0M7.1 — an in-flight build order attached to an Army.
 *  v0M7.5b (2026-07-26) — extended with optional `roadPath` for the
 *  Road polyline mechanic per `MAP.md` §9.1. When `kind='road'` and
 *  `roadPath` is non-empty, the build system treats `targetCell` as
 *  the CURRENT road cell being built; on its completion, the first
 *  element of `roadPath` is popped and becomes the new `targetCell`
 *  (with `progressDays` reset to 0 + `army.plannedPath` auto-set to
 *  the new cell), and the Army walks to the next cell. When
 *  `roadPath` is empty and the current cell completes, the order
 *  clears normally. Other kinds (Watch Tower / Fort / Bridge) never
 *  set `roadPath` and behave as single-cell placements. */
export interface BuildOrder {
  readonly kind: WorldStructureKind;
  targetCell: { readonly x: number; readonly y: number };
  /** Accumulated game-days of progress toward completion. Resets to 0
   *  if the Army moves off the target cell; persists across battles
   *  (battle system just pauses accrual). */
  progressDays: number;
  /**
   * v0M7.5b — remaining road cells after `targetCell` (in build order).
   * Only set for `kind='road'` polyline construction. Undefined /
   * empty = single-cell buildable (the default for Watch Tower /
   * Fort / Bridge, and for a 1-cell Road).
   */
  roadPath?: readonly { readonly x: number; readonly y: number }[];
}

/** Create a fresh Army with an empty cohort roster at the given position. */
// v0M10.1 (2026-07-24) — Attachment type union. Extend the array as
// Michael adds new attachment kinds. Kept in sync with the client-side
// `AVAILABLE_ATTACHMENTS` list in army-info-panel.ts.
export const ATTACHMENT_TYPES = ['Engineer', 'Healer', 'Supply Wagon'] as const;
export type AttachmentType = typeof ATTACHMENT_TYPES[number];

export function createArmy(
  id: ArmyId,
  factionId: FactionId,
  commanderId: CommanderId,
  position: { x: number; y: number },
): Army {
  return {
    id,
    ownerFactionId: factionId,
    commanderId,
    position,
    movementGoal: null,
    plannedPath: null,
    cohortIds: [],
    currentBattleId: null,
    // v0M10.1 (2026-07-24) — attachments + supply defaults. Values here
    // mirror the base dials (army_supply_cap, army_attachment_slot_cap,
    // new_army_supply_cost); reading the dial live at createArmy would
    // introduce a cyclic import (state → data/dials → …), so the
    // constants are inlined. Diffs against the dial are caught by the
    // recovery-loop test that ticks a fresh army.
    //
    // v0M10.4 (2026-07-24) — new-army supply cost = 600 supplies. That
    // deducted 600 IS the starting pool of the new army (per Michael's
    // "start with 600 Supply" rule). Scenario-bootstrap armies also
    // seed at 600 for consistency.
    attachments: [],
    attachmentCap: 1,
    supplies: 600,
    supplyCap: 1200,
  };
}

// ---------------------------------------------------------------------------
// Spy — v0M5.3 standalone special-unit entity (2026-07-25)
// ---------------------------------------------------------------------------

/**
 * Standalone map entity per `ESPIONAGE.md` §1 / `MAP.md` §7.5. Distinct
 * from Cohorts (recruited into Armies) and army attachments — Spies
 * occupy their own map position and carry ability tags
 * (`Invisibility` + `Detection` per SpecialUnits xlsx row).
 *
 * Movement mirrors Army: continuous float position, LOS-smoothed
 * pathfinder plan. Spies contribute vision to their owning faction's
 * visibility map (v0M5.1); their `Detection` tag lets that vision
 * reveal invisibles (state 4 per MAP.md §7.1).
 *
 * v0M5.4 (following) adds `insideSettlementId` + infiltration ladder;
 * v0M5.3 ships just the free-moving map entity.
 */
export interface Spy {
  readonly id: SpyId;
  readonly ownerFactionId: FactionId;
  /**
   * SpecialUnits xlsx row name (matches loadSpecialUnits() key). 'Spy'
   * at v1; future rows (post-v0M7 world-buildables) may add other
   * standalone special units.
   */
  readonly specialUnitType: string;
  /** Continuous float position in cell units. Mirrors Army.position. */
  position: { x: number; y: number };
  /**
   * Final march destination for the current move order, or null when
   * idle. Cleared on arrival within ARRIVAL_EPSILON of the goal.
   */
  movementGoal: { x: number; y: number } | null;
  /**
   * Smoothed waypoint list from the current position toward
   * movementGoal, in cell coords. Mirrors Army.plannedPath.
   */
  plannedPath: readonly (readonly [number, number])[] | null;

  /**
   * MFix12.4 (2026-07-27) — chained player-set destinations queued after
   * the current `movementGoal`. Mirrors Army.waypoints. Consumed on
   * arrival by movement.ts; cleared by a plain (non-append) `move_spy`.
   */
  waypoints?: readonly { readonly x: number; readonly y: number }[];

  /**
   * v0M5.4 (2026-07-25) — Settlement the Spy is currently inside, or
   * null if on the map. When non-null the Spy is not rendered on the
   * map, does not contribute vision to its cell, and cannot be
   * pathfound to (moving requires exit first).
   *
   * Enter transitions position to (settlement.cell.x + 0.5, cell.y +
   * 0.5) even while insideSettlementId is set — so `exit_settlement_spy`
   * simply clears the field to re-materialize the spy at that cell.
   *
   * Optional so pre-v0M5.4 fixtures compile.
   */
  insideSettlementId?: SettlementId | null;

  /**
   * v0M5.4 (2026-07-25) — Days elapsed since the Spy entered the
   * current settlement. Incremented by the espionage system while
   * `insideSettlementId !== null`; reset to 0 on exit. Consumed by the
   * settlement panel to drive the infiltration ladder progress bar
   * (Level 1 at `infiltration_days_per_level` from SpecialUnits xlsx,
   * Level 2 at 2×, Level 3 at 3×).
   *
   * Optional; missing = 0.
   */
  infiltrationDays?: number;

  /**
   * v0M5.4 (2026-07-25) — True if the Spy is actively performing
   * Counter-Espionage. Only settable when the Spy is inside a friendly
   * settlement. Consumed by the espionage system: each day, for every
   * enemy Invisibility-tagged entity in the same settlement, a
   * `counter_espionage_chance` roll fires; on success the enemy spy
   * is killed and an event log entry is emitted.
   *
   * Optional; missing = false. Set to false automatically on
   * `exit_settlement_spy`.
   */
  counterEspionage?: boolean;

  /**
   * v0M5.5 (2026-07-25) — Steal order (Foreign Accounts knowledge
   * unlock). While true, the espionage system transfers a growing
   * percentage of the enemy settlement's per-day gold income from the
   * enemy faction to the Spy's owning faction at the day boundary.
   * Percentage grows over time inside the settlement per
   * `steal_base_pct` + `steal_increment_pct` every `steal_increment_days`,
   * capped at 100%. Mutually exclusive with `counterEspionage` (Steal
   * targets an enemy settlement; CE targets a friendly one — the
   * spatial exclusivity is enforced by settlement ownership).
   *
   * Optional; missing = false. Cleared on exit + on Spy death.
   */
  stealActive?: boolean;

  /**
   * v0M5.5 (2026-07-25) — Days that the current Steal order has been
   * active. Incremented alongside `infiltrationDays` while
   * `stealActive` is true. Consumed by the espionage system to compute
   * the tier: `pct = min(1.0, base + increment × floor(days / increment_days))`.
   * Reset to 0 when Steal is toggled on afresh. Optional; missing = 0.
   */
  stealActiveDays?: number;

  /**
   * v0M5.5 (2026-07-25) — Sabotage target — index into
   * `province.structures` of the completed structure the Spy is
   * currently sabotaging. Non-null while a Sabotage order is
   * in-flight; set by `sabotage_structure_spy` command. Mutually
   * exclusive with Steal + CE (a Spy can hold at most one active
   * order). Cleared on completion, on exit, or on Spy death.
   *
   * Optional; missing = null.
   */
  sabotageTargetStructureIndex?: number | null;

  /**
   * v0M5.5 (2026-07-25) — Game-days remaining before the current
   * Sabotage order completes. Decremented at the day boundary while
   * `sabotageTargetStructureIndex !== null`. On reaching ≤ 0: the
   * target structure is destroyed, the owner is alerted via event
   * log, and the order is cleared. Optional; missing / null when no
   * Sabotage in flight.
   */
  sabotageDaysRemaining?: number | null;

  /**
   * MFix11.2+.3 (2026-07-27) — Pending "escort + auto-enter" intent.
   * Set by `set_spy_enter_target` (Michael's RMB-on-settlement flow):
   * spy paths toward the target settlement's closest adjacent cell,
   * and the movement system auto-fires the equivalent of
   * `enter_settlement_spy` when Chebyshev distance ≤ 1.5. Cleared on
   * arrival (auto-enter fires), on manual `move_spy` override, or on
   * ownership drift (see `enterOnArrivalOwnerAtIntent`).
   *
   * Optional; missing / null when no intent is queued.
   */
  enterOnArrivalSettlementId?: SettlementId | null;

  /**
   * MFix11.2+.3 (2026-07-27) — Owner of the target settlement's
   * province at the moment `set_spy_enter_target` was issued. Compared
   * per-tick against the current owner; a mismatch invalidates the
   * player's original intent (the target flipped hands between order
   * and arrival) and clears both pending fields without entering.
   *
   * Nullable to accept neutral targets (province.ownerFactionId can
   * be null). Optional; missing when no intent is queued.
   */
  enterOnArrivalOwnerAtIntent?: FactionId | null;
}

/**
 * Create a fresh Spy. Position matches the recruiting settlement's cell
 * by default; consumers pass the exact spawn coords. `specialUnitType`
 * is the SpecialUnits xlsx row key (Spy at v1). No supplies / no
 * combat stats — Spies never fight and never draw supply.
 */
export function createSpy(
  id: SpyId,
  factionId: FactionId,
  specialUnitType: string,
  position: { x: number; y: number },
): Spy {
  return {
    id,
    ownerFactionId: factionId,
    specialUnitType,
    position,
    movementGoal: null,
    plannedPath: null,
  };
}

// ---------------------------------------------------------------------------
// WorldStructure — v0M7.1 world-buildable entity (2026-07-25)
// ---------------------------------------------------------------------------

/**
 * Player-built map-cell structure per `MAP.md` §9. Four v1 kinds:
 * `road` / `watchtower` / `bridge` / `fort`. Effect + ownership rules
 * vary per-kind (see MAP.md §9.2 table); shared shape:
 *
 *   - Unique `id` + fixed integer `cell` position (never moves).
 *   - `ownerFactionId` = null for kinds without an owner (Road at v1;
 *     Bridge treated as owner-tracked-but-effect-universal at v1).
 *   - `garrisonCohortIds` populated only for `fort` (mirrors
 *     `Settlement.garrisonCohortIds`).
 *   - `captureProgress` populated only when an enemy Army is actively
 *     parked on a park-capturable kind (Watch Tower); cleared when
 *     the enemy leaves or the timer completes.
 *
 * Persistence rules: built by `spawnWorldStructure` in `build.ts` on
 * order completion; deleted on destroy-completion (Watch Tower) or
 * post-capture-flip if destroy mode picked.
 */
export interface WorldStructure {
  readonly id: WorldStructureId;
  readonly kind: WorldStructureKind;
  /** null for permanent-unowned kinds (Road at v1). Otherwise the
   *  faction that built it or last captured it. */
  ownerFactionId: FactionId | null;
  /** Integer cell coordinates. WorldStructures occupy their cell
   *  centre; hit-test / effect lookups match by (Math.floor(x), Math.floor(y)). */
  readonly cell: { readonly x: number; readonly y: number };
  /** Fort garrison per BATTLES.md §3 pattern. Empty for other kinds. */
  garrisonCohortIds?: CohortId[];
  /** In-flight capture attempt against this structure (Watch Tower
   *  park-capture at v1). Null when no enemy is contesting. */
  captureProgress?: CaptureProgress | null;
  /**
   * v0M7.4d+siege2 (2026-07-26) — Active siege on this Fort (parity
   * with `Settlement.siegeState` per BATTLES.md §3.4). `null`/absent
   * when no siege. Semantics reuse `SiegeState`:
   *   - Opened when a hostile Army moves adjacent to the Fort cell
   *     and no defending friendly-field Army is on the Fort cell.
   *   - Countdown decrements by `1 / TICKS_PER_GAME_DAY` per tick.
   *   - Cleared when: sieger leaves adjacency, sieger destroyed,
   *     defender field Army contacts sieger, OR daysRemaining ≤ 0
   *     (surrender path — Fort owner flips + garrison destroyed,
   *     matching the assault outcome per session-33 elicited scope).
   *
   * Surrender timer sourced from the `fort_surrender_days` dial;
   * fallback constant in apply-commands.ts. Kind='fort' only —
   * other WorldStructureKinds don't participate.
   *
   * Optional/back-compat: undefined on non-Fort kinds and on
   * pre-v0M7.4d+siege2 fixtures.
   */
  siegeState?: SiegeState | null;
}

/**
 * v0M7.1 — Park-capture progress state. `days` advances at the day
 * boundary while any enemy Army of `byFactionId` is on the structure's
 * cell. On reaching `MapStructureSpec.captureDays`, ownership flips
 * (mode='capture') or the structure is deleted (mode='destroy').
 * `mode` is set at capture-order issue time by the attacker via the
 * command layer — Watch Tower defaults to 'capture' if the attacker
 * doesn't issue an explicit destroy order.
 */
export interface CaptureProgress {
  readonly byFactionId: FactionId;
  days: number;
  mode: 'capture' | 'destroy';
}

/** Create a fresh WorldStructure. Garrison + capture state default to
 *  empty; population-adjusted at higher-level (Fort sets an empty
 *  garrisonCohortIds; other kinds omit the field). */
export function createWorldStructure(
  id: WorldStructureId,
  kind: WorldStructureKind,
  ownerFactionId: FactionId | null,
  cell: { x: number; y: number },
): WorldStructure {
  const ws: WorldStructure = {
    id,
    kind,
    ownerFactionId,
    cell: { x: Math.floor(cell.x), y: Math.floor(cell.y) },
    captureProgress: null,
  };
  // Fort is the only garrisonable kind at v1 per MAP.md §9.2.
  if (kind === 'fort') ws.garrisonCohortIds = [];
  return ws;
}

// ---------------------------------------------------------------------------
// Sim timing constants (v0M3.2 economy)
// ---------------------------------------------------------------------------

/**
 * Game-time seconds represented by a single sim tick. Derived from
 * MECHANICS.md §1.1: 1 game-day = 5 ticks; at 1× speed 1 game-day
 * equals 1 wall-second (dial), so each tick represents 0.2 game-time
 * seconds. Economy rates (per second) multiply by this constant per
 * tick.
 *
 * If the design ever changes the ticks-per-game-day ratio, update here
 * and every economy production/consumption call site will pick up the
 * new value automatically.
 */
export const SECONDS_PER_TICK = 0.2;

/**
 * v0MFix3.8 — Ticks per game-day (was implicit 5; now 25).
 *
 * With `SECONDS_PER_TICK = 0.2`, this makes 1 game-day = 5 game-time
 * seconds. Sim-driver tick cadence is unchanged at 5 Hz at 1×, so:
 *   - 1 wall-second at 1× = 5 ticks = 1/5 of a game-day
 *   - 1 game-day at 1× = 5 wall-seconds (was 1 wall-second)
 * Michael's playtest ask: slow the visible pace so player has time to
 * observe day-scale events (recruitment, sieges, battles).
 *
 * Consumers:
 *   - `main.ts` day counter uses `state.tick / TICKS_PER_GAME_DAY`
 *   - `sieges.ts` timer decrements by `1 / TICKS_PER_GAME_DAY` per tick
 *   - `ai.ts` cadence gates on `state.tick % TICKS_PER_GAME_DAY === 0`
 *   - `economy.ts` per-day recovery rates divide by
 *     `TICKS_PER_GAME_DAY` (was `× SECONDS_PER_TICK` which coincidentally
 *     equalled `/ 5` under the old 5-ticks/day model).
 */
export const TICKS_PER_GAME_DAY = 25;

/**
 * v0MFix3.8 — Game-time seconds per game-day. Derived: 25 × 0.2 = 5.
 * Consumers that want per-day rates from per-game-second dials multiply
 * by this constant (e.g. resource-hud showing per-day income from
 * `gold_per_citizen_per_sec`-based `computeFactionIncome`).
 */
export const SECONDS_PER_GAME_DAY = TICKS_PER_GAME_DAY * SECONDS_PER_TICK;

// ---------------------------------------------------------------------------
// Army movement helpers (v0M3.1)
// ---------------------------------------------------------------------------

/**
 * Placeholder Army speed until per-cohort speeds land — 0.5 cells/tick
 * at 16 km cells + 5 ticks/day = 40 km/game-day, a realistic ancient
 * army march. A first-playtest dial candidate per MECHANICS.md §3.1.
 *
 * Marked exported so the movement system, apply-commands, and tests
 * agree on the same constant.
 */
export const DEFAULT_ARMY_SPEED_CELLS_PER_TICK = 0.5;

/**
 * Distance (in cell units) within which an Army is considered to have
 * arrived at its `movementGoal`. Prevents jitter around the goal cell
 * when the last movement step overshoots.
 */
export const ARRIVAL_EPSILON = 0.5;

// ---------------------------------------------------------------------------
// v0M9 (2026-07-27) — Minor-settlement pillage / repair dial fallbacks
// ---------------------------------------------------------------------------
// These are code-side fallbacks; Michael will author the same keys on the
// Balance.xlsx Dials sheet post-milestone, and `safeDialNumber(key, fallback)`
// wrappers read the xlsx value when present. Xlsx-owed at v0M9-close.

/**
 * v0M9 — Game-days of continuous enemy ZoC presence required to pillage
 * a minor settlement. Compares against `Settlement.pillageState.daysElapsed`.
 */
export const MINOR_SETTLEMENT_PILLAGE_DAYS = 30;

/**
 * v0M9 — Game-days of no-enemy-in-ZoC repair work required to restore a
 * pillaged minor settlement. Compares against
 * `Settlement.repairState.daysElapsed`.
 */
export const MINOR_SETTLEMENT_REPAIR_DAYS = 5;

/**
 * v0M9 — Gold cost paid up-front by the province owner to start a
 * repair. Not refunded on cancel or on repair-pause-indefinitely.
 */
export const MINOR_SETTLEMENT_REPAIR_GOLD_COST = 100;

/**
 * v0M9 — Structure cap for a minor settlement. Compare with the tier-based
 * caps used for Province Capitals (Village=3 / Town=6 / City=10 per
 * ECONOMY.md §2.1). Minors are Village-tier but capped lower than a
 * Village capital by design.
 */
export const MINOR_SETTLEMENT_STRUCTURE_CAP = 2;

/**
 * v0M9-followup.3 (2026-07-27) — Garrison cap for a minor settlement.
 * Independent from `garrison_max_per_settlement` (which caps major-city
 * garrisons). Michael's design lock: Village-tier semantics, a token
 * defender by default. Xlsx key: `minor_settlement_garrison_max`.
 */
export const MINOR_SETTLEMENT_GARRISON_MAX = 1;

/**
 * Effective per-tick march speed for an Army.
 *
 * **v0M3.1 lock (per Michael, 2026-07-11):** an Army's effective speed
 * is the minimum of its Cohort speeds — a composite army feels its
 * composition by marching at the slowest cohort.
 *
 * **Session-27 (2026-07-24) — landed.** Reads per-cohort
 * `moveSpeedCampaign` from `Balance.xlsx Cohorts` sheet (via
 * `src/data/cohorts.ts::loadCohorts()`) and returns the minimum across
 * the Army's cohorts. Armies with no cohorts (e.g. bare test fixtures
 * or future Spy units before their placeholder cohort lands) fall
 * through to `DEFAULT_ARMY_SPEED_CELLS_PER_TICK`.
 *
 * Route-preference and stance modifiers (Fastest/Direct/Concealed,
 * Forced march ×1.35, Careful ×0.75) multiply this base and apply
 * inside the movement system, not here.
 */
export function armyEffectiveSpeed(army: Army, state: GameState): number {
  if (army.cohortIds.length === 0) return DEFAULT_ARMY_SPEED_CELLS_PER_TICK;
  const COHORTS = COHORTS_CACHE;
  let slowest = Infinity;
  for (const cid of army.cohortIds) {
    const cohort = state.cohorts.get(cid);
    if (!cohort) continue;
    const spec = COHORTS[cohort.type];
    if (!spec) continue;
    const s = spec.moveSpeedCampaign;
    if (Number.isFinite(s) && s < slowest) slowest = s;
  }
  return Number.isFinite(slowest) ? slowest : DEFAULT_ARMY_SPEED_CELLS_PER_TICK;
}

// Session-27 (2026-07-24): eager load of the xlsx-authoritative cohort
// spec dictionary. Cost = a single JSON parse at module init; the
// dials.json blob is already loaded once by loader.ts anyway.
const COHORTS_CACHE = loadCohorts();

// ---------------------------------------------------------------------------
// GameState — top-level authoritative sim shape
// ---------------------------------------------------------------------------

export interface GameState {
  /** Monotonic tick counter. 5 ticks per game-day per MECHANICS.md §1.1. */
  tick: number;

  /** Seeded RNG state; owned by the sim, threaded through systems. */
  rng: RngState;

  /** Campaign seed (for deterministic replay + save-game validation). */
  readonly seed: number;

  /** Entity Maps. Iteration is via sortedIds() below — never raw .values(). */
  readonly factions: Map<FactionId, Faction>;
  readonly provinces: Map<ProvinceId, Province>;
  readonly settlements: Map<SettlementId, Settlement>;
  readonly armies: Map<ArmyId, Army>;
  readonly commanders: Map<CommanderId, Commander>;
  /** Campaign-scope Cohort entities (v0M3.2a). Battlescape hydration via `type` lookup. */
  readonly cohorts: Map<CohortId, Cohort>;
  /**
   * v0M5.3 (2026-07-25) — Standalone special-unit entities (Spy at v1).
   * Distinct from Cohorts / Armies / attachments; each Spy occupies its
   * own strategic-map position and carries ability tags from the
   * SpecialUnits xlsx row. Iteration deterministic via sortedIds().
   *
   * Optional so pre-v0M5.3 fixtures compile without a Map allocation;
   * consumers must coalesce `state.spies ?? new Map()`.
   */
  spies?: Map<SpyId, Spy>;

  /**
   * v0M7.1 (2026-07-25) — World-buildable entities per `MAP.md` §9
   * (Watch Tower / Fort at v0M7 stateful set; Road / Bridge are also
   * WorldStructures but carry no per-instance ownership state — they're
   * still minted as entries so the map layer + movement/vision systems
   * can look them up by cell). Iteration deterministic via sortedIds().
   *
   * Optional so pre-v0M7 fixtures compile without a Map allocation;
   * consumers must coalesce `state.worldStructures ?? new Map()`.
   */
  worldStructures?: Map<WorldStructureId, WorldStructure>;

  /**
   * Monotonic id counters — used when the sim needs to create a new
   * entity (e.g., recruitment spawning a Cohort or Army). Bumped on
   * every allocation; deterministic across MP hosts because the same
   * command sequence produces the same allocation order.
   */
  nextCohortId: number;
  nextArmyId: number;
  nextCommanderId: number;
  nextBattleId: number;
  /** v0M5.3 (2026-07-25) — Monotonic Spy id counter. Bumped on every
   *  spy recruit (post-recruit-time completion). Optional so pre-v0M5.3
   *  fixtures compile; falls back to 1 when absent. */
  nextSpyId?: number;
  /** v0M7.1 (2026-07-25) — Monotonic WorldStructure id counter. Bumped
   *  on every completed build. Optional so pre-v0M7 fixtures compile;
   *  falls back to 1 when absent. */
  nextWorldStructureId?: number;

  /**
   * Active battles (v0M3.3). Keyed by BattleId. Added when two Armies of
   * different teams enter ZoC overlap per MECHANICS.md §3.2 (ZoC-triggered
   * creation is a follow-up milestone; v0M3.3 tests construct battles
   * manually via `createBattle`). Battles step at BATTLES_ROUNDS_PER_TICK
   * per world tick via `battlesSystem`; concluded battles are removed.
   *
   * Battle state is imported from `./battle-state` at type-only level to
   * keep this module free of circular refs — see the top-level re-export
   * from `./index.ts` for consumers.
   */
  readonly battles: Map<BattleIdImport, import('./battle-state').Battle>;

  /**
   * Victory result (v0M3.9). Set by the `victory` system each tick based
   * on `SimContext.victoryCondition`. Client polls this to trigger the
   * victory banner + freeze the sim driver. Ongoing = the game is still
   * being played; team-victory / draw = game ended.
   */
  victoryResult: VictoryResult;

  /**
   * v0M5.1 (2026-07-25) — Per-faction visibility maps. Keyed by
   * FactionId. Recomputed each tick by the `visibility` system via
   * BFS from every viewer (armies + owned settlements + future spies)
   * with terrain-cost propagation per `MAP.md` §7. Team pooling folds
   * teammate maps together (§7.4). Renderer at v0M5.2 reads the local
   * player-faction map for the 4-state fog layer.
   *
   * Optional so existing test fixtures + retroactive `createEmptyState`
   * callers compile; the visibility system tolerates missing maps by
   * lazily creating them from `SimContext.terrain`. Scenario
   * bootstrap at v0M5 seeds every non-neutral faction with a fresh
   * map (initial state per `scenario.startExplored`).
   */
  visibility?: Map<FactionId, import('./visibility').VisibilityMap>;
}

/**
 * Union of possible game-end states. `ongoing` is the initial value at
 * scenario boot; the victory system mutates to `team-victory` or `draw`
 * when the check fires.
 */
export type VictoryResult =
  | { readonly kind: 'ongoing' }
  | { readonly kind: 'team-victory'; readonly winningTeamId: number; readonly condition: 'destroy-capitals' | 'destroy-cities' }
  | { readonly kind: 'draw'; readonly condition: 'destroy-capitals' | 'destroy-cities' }
  /**
   * MFix11.5 (2026-07-27) — human player was eliminated but the
   * broader victory hasn't resolved yet (multiple AI teams still
   * fighting). Without this variant the sim would stay 'ongoing'
   * forever from the player's POV and no banner would ever appear.
   * Client treats this as a terminal "you lost" state and pauses.
   */
  | { readonly kind: 'player-defeated'; readonly condition: 'destroy-capitals' | 'destroy-cities' };

// Re-export BattleId here so GameState.battles typing is complete.
// (BattleId lives in battle-state.ts to keep battle types cohesive.)
export type { BattleId } from './battle-state';
export { toBattleId } from './battle-state';

// ---------------------------------------------------------------------------
// Deterministic iteration helper (MECHANICS.md §1.1 rule 3)
// ---------------------------------------------------------------------------

/**
 * Return the ids of a state Map sorted numerically. Systems that iterate
 * over entities must use this helper, not raw `.values()`, so iteration
 * order is stable across engines and MP hosts.
 */
export function sortedIds<K extends number, V>(map: ReadonlyMap<K, V>): readonly K[] {
  return [...map.keys()].sort((a, b) => a - b);
}

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

/**
 * Fresh empty GameState for tests and scenario bootstrap. Real scenario
 * generation (with factions, provinces, cities, and starting armies)
 * lands with scenario.ts at a later milestone.
 */
export function createEmptyState(seed: number, rng: RngState): GameState {
  return {
    tick: 0,
    rng,
    seed,
    factions: new Map(),
    provinces: new Map(),
    settlements: new Map(),
    armies: new Map(),
    commanders: new Map(),
    cohorts: new Map(),
    battles: new Map(),
    nextCohortId: 1,
    nextArmyId: 1,
    nextCommanderId: 1,
    nextBattleId: 1,
    victoryResult: { kind: 'ongoing' },
  };
}
